import React, { useState } from 'react';
import { User, Phone, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { HostGuest } from '../../types/hostGuest';
import { useAppSelector } from '../../store/hooks';
import Modal from '../UI/Modal';
import Button from '../UI/Button';

interface HostGuestViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  guest: HostGuest;
}

const HostGuestViewModal: React.FC<HostGuestViewModalProps> = ({
  isOpen,
  onClose,
  guest
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  // Companions state
  const [companionsExpanded, setCompanionsExpanded] = useState(true);
  const [companionPage, setCompanionPage] = useState(1);
  const companionPageSize = 5;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Guest Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Guest Info Section */}
        <div className="space-y-4">
          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Guest Information
          </h3>
          
          <div className={`p-4 rounded-lg border ${
            theme === 'dark' 
              ? 'bg-gray-800/50 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-4">
              <div className={`h-12 w-12 flex-shrink-0 rounded-full flex items-center justify-center ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <User className={`h-6 w-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-base font-medium ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {guest.name || 'Unnamed Guest'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Phone className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {guest.phone || 'No phone number'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Divider */}
        <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`} />
        
        {/* Companions Section */}
        {guest.companion_count > 0 ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setCompanionsExpanded(!companionsExpanded)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-800 hover:bg-gray-750' 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-2">
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Companions
                </h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}>
                  {guest.companion_count}
                </span>
              </div>
              {companionsExpanded ? (
                <ChevronUp className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              ) : (
                <ChevronDown className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              )}
            </button>
            
            {companionsExpanded && (
              <div className="space-y-3">
                {(() => {
                  const totalCompanions = guest.companions.length;
                  const totalPages = Math.ceil(totalCompanions / companionPageSize);
                  const startIndex = (companionPage - 1) * companionPageSize;
                  const endIndex = startIndex + companionPageSize;
                  const paginatedCompanions = guest.companions.slice(startIndex, endIndex);
                  
                  return (
                    <>
                      {paginatedCompanions.map((companion, index) => (
                        <div
                          key={companion.id}
                          className={`p-4 rounded-lg border ${
                            theme === 'dark' 
                              ? 'bg-gray-800/50 border-gray-700' 
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 flex-shrink-0 rounded-full flex items-center justify-center ${
                              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`}>
                              <span className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                {startIndex + index + 1}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {companion.name || 'Unnamed Companion'}
                              </p>
                              <p className={`text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {companion.phone || 'No phone'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Companion Pagination */}
                      {totalPages > 1 && (
                        <div className={`flex items-center justify-between pt-3 border-t ${
                          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                          <span className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Showing {startIndex + 1}-{Math.min(endIndex, totalCompanions)} of {totalCompanions}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setCompanionPage(p => Math.max(1, p - 1))}
                              disabled={companionPage === 1}
                              className={`p-1 rounded ${
                                companionPage === 1
                                  ? theme === 'dark' ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                                  : theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>
                            <span className={`text-sm ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              {companionPage} / {totalPages}
                            </span>
                            <button
                              type="button"
                              onClick={() => setCompanionPage(p => Math.min(totalPages, p + 1))}
                              disabled={companionPage === totalPages}
                              className={`p-1 rounded ${
                                companionPage === totalPages
                                  ? theme === 'dark' ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                                  : theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ) : (
          <div className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="text-sm">No companions</p>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HostGuestViewModal;
